"use client";

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useSearchParams } from "next/navigation";
import { motion, useReducedMotion } from "motion/react";
import { ArrowRight, Check, ChevronRight, Eye, X } from "lucide-react";
import { DISPLAY_FONT, DojoFrame, FreqBar169, HandMatrix169, heroCellLabel, Rise, Stagger } from "@/components/dojo";
import { cn } from "@/lib/cn";
import {
  EMPTY_RECORD,
  getQuizStats,
  getRecordSnapshot,
  subscribeRecord,
  writeRecord,
} from "@/lib/quiz-progress";
import type { QuizResult } from "@/lib/quiz-progress";
import type { QuizQuestion, QuizResponse } from "@/lib/quiz-types";
import { renderWithGloss } from "@/components/quiz-gloss";
import { splitGluedAnswer, stripLatex } from "@/lib/quiz-sanitize";
import { teachText } from "@/lib/teach-text";
import { PokerTable } from "@/components/poker-table";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";

// ─── 设计基调:灯下道场 ────────────────────────────────────────────────────────
// 与 /train 同一盏灯:绿炭毡底 + 头顶暖金灯。题面是落上绿毡的米色牌卡,
// 选项是毡上的动作按钮;答对镀金,答错落玫瑰。解析收进折叠格,不当正文。

const DIFFICULTY_LABEL: Record<number, string> = {
  1: "◆ 入门",
  2: "◆◆ 进阶",
  3: "◆◆◆ 高级",
};

function StatusDot({ result }: { result?: QuizResult }) {
  return (
    <span
      className={cn(
        "size-2 shrink-0 rounded-full",
        !result && "bg-[#2A3D30]",
        result === "correct" && "bg-emerald-400",
        result === "wrong" && "bg-rose-400",
        result === "revealed" && "bg-amber-300/70",
      )}
    />
  );
}

function CardImage({ src, fallback, alt }: { src: string; fallback: string; alt: string }) {
  const [cur, setCur] = useState(src);
  if (!cur) return null;
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={cur}
      alt={alt}
      onError={() => {
        if (fallback && cur !== fallback) setCur(fallback);
      }}
      className="w-full rounded-lg border border-[#2A3D30] bg-[#0C130F]"
    />
  );
}

/** 下一题:翡翠主行动,与 /train 同一颗按钮 */
function NextButton({ onNext, label }: { onNext: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onNext}
      className="group inline-flex items-center gap-2 rounded-xl bg-emerald-400 px-6 py-2.5 text-sm font-bold text-[#062015] shadow-[0_10px_36px_-10px_rgba(52,211,153,0.5)] transition-all duration-150 hover:bg-emerald-300 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090E0B]"
    >
      {label}
      <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
    </button>
  );
}

/** 解析:折叠格,不当正文(视觉优先,文字退为脚注) */
function ExplanationDetails({ text }: { text: string }) {
  return (
    <details className="group mt-3 rounded-xl border border-white/5 bg-black/20">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-xs font-medium text-stone-400 transition-colors hover:text-stone-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-300/60 [&::-webkit-details-marker]:hidden">
        <ChevronRight className="size-3.5 transition-transform duration-200 group-open:rotate-90" />
        看解析
      </summary>
      <div className="border-t border-white/5 px-4 py-3.5">
        <p className="whitespace-pre-line text-sm leading-relaxed text-stone-400">
          {renderWithGloss(text)}
        </p>
      </div>
    </details>
  );
}

function AnswerPanel({
  question,
  onRecord,
  onNext,
  nextLabel,
}: {
  question: QuizQuestion;
  onRecord: (id: string, result: QuizResult) => void;
  onNext: () => void;
  nextLabel: string;
}) {
  const isChoice = question.options.length > 0;
  const [picked, setPicked] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [graded, setGraded] = useState<"correct" | "wrong" | null>(null);
  const [side, setSide] = useState<"front" | "back">("front");
  const reduce = useReducedMotion();

  // 展示层止血:原料题库有「答案\n【解析】…」粘连与 LaTeX 残留,渲染前拆洗(幂等)
  const cleaned = useMemo(
    () => splitGluedAnswer(question.answer, question.explanation),
    [question.answer, question.explanation],
  );

  const answered = picked !== null;
  const showAnswer = isChoice ? answered : revealed;
  const isCorrectPick = answered && question.answer_keys.includes(picked || "");

  const pick = (key: string) => {
    if (answered) return;
    setPicked(key);
    onRecord(question.id, question.answer_keys.includes(key) ? "correct" : "wrong");
    setSide("back");
  };

  /** 开放题:先揭示,不记账;自评才落 correct / wrong */
  const reveal = () => {
    setRevealed(true);
    setSide("back");
  };

  const grade = (r: "correct" | "wrong") => {
    if (graded) return;
    setGraded(r);
    onRecord(question.id, r);
  };

  return (
    <div className="space-y-4">
      {/* 选择题:毡上的动作按钮 */}
      {isChoice && (
        <div className="grid gap-3 sm:grid-cols-2" role="group" aria-label="选择答案">
          {question.options.map((opt) => {
            const isPicked = picked === opt.key;
            const isAnswer = question.answer_keys.includes(opt.key);
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => pick(opt.key)}
                disabled={answered}
                className={cn(
                  "relative flex w-full items-start gap-3 rounded-xl border px-4 py-3.5 text-left transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090E0B]",
                  answered && isAnswer &&
                    "border-amber-300/70 bg-[#261E0D] text-amber-100 shadow-[0_0_44px_-10px_rgba(227,181,91,0.45)]",
                  answered && isPicked && !isAnswer &&
                    "border-rose-400/60 bg-[#271114] text-rose-100",
                  answered && !isPicked && !isAnswer &&
                    "border-[#223329] bg-[#0E1712] text-stone-500 opacity-45",
                  !answered &&
                    "border-[#243528] bg-[#101B14] text-stone-100 hover:-translate-y-0.5 hover:border-emerald-300/40 hover:bg-[#14231A] active:scale-[0.98] active:translate-y-0",
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold",
                    answered && isAnswer
                      ? "border-amber-300/70 text-amber-300"
                      : answered && isPicked
                        ? "border-rose-400/70 text-rose-300"
                        : "border-[#2A3D30] text-stone-400",
                  )}
                >
                  {opt.key}
                </span>
                <span className="min-w-0 flex-1 text-sm leading-relaxed">{renderWithGloss(opt.text)}</span>
                {answered && isAnswer && (
                  <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-amber-300/20 text-amber-300">
                    <Check className="size-3.5" />
                  </span>
                )}
                {answered && isPicked && !isAnswer && (
                  <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-rose-400/20 text-rose-300">
                    <X className="size-3.5" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* 开放题:先想一想,再揭示 */}
      {!isChoice && !revealed && (
        <button
          type="button"
          onClick={reveal}
          className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/40 bg-emerald-400/10 px-5 py-3 text-sm font-semibold text-emerald-200 transition-all duration-150 hover:bg-emerald-400/20 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090E0B]"
        >
          <Eye className="size-4" />
          先想一想,再揭示答案
        </button>
      )}

      {/* 反馈:答对镀金,答错落玫瑰 */}
      {showAnswer && (
        <motion.div
          initial={{ opacity: 0, y: reduce ? 0 : 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="space-y-4"
          aria-live="polite"
        >
          <div
            className={cn(
              "rounded-2xl border p-5",
              isChoice && isCorrectPick &&
                "border-amber-300/35 bg-[#1B1508]/90 shadow-[0_0_70px_-18px_rgba(227,181,91,0.4)]",
              isChoice && !isCorrectPick && "border-rose-400/30 bg-[#1E1013]/90",
              !isChoice && graded === "correct" &&
                "border-amber-300/35 bg-[#1B1508]/90 shadow-[0_0_70px_-18px_rgba(227,181,91,0.4)]",
              !isChoice && graded === "wrong" && "border-rose-400/30 bg-[#1E1013]/90",
              !isChoice && !graded && "border-[#2A3D30] bg-[#0D1510]/90",
            )}
          >
            {isChoice && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span
                  className={cn(
                    "flex size-7 shrink-0 items-center justify-center rounded-full",
                    isCorrectPick
                      ? "bg-amber-300/15 text-amber-300"
                      : "bg-rose-400/15 text-rose-300",
                  )}
                >
                  {isCorrectPick ? <Check className="size-3.5" /> : <X className="size-3.5" />}
                </span>
                <span
                  className={cn(
                    "text-sm font-bold",
                    isCorrectPick ? "text-amber-200" : "text-rose-200",
                  )}
                >
                  {isCorrectPick ? "回答正确" : "这题没选对"}
                </span>
                <span className="text-xs text-stone-500">
                  正确答案:{question.answer_keys.join(" 或 ")}
                </span>
              </div>
            )}

            {!isChoice && (
              <p className="text-sm leading-relaxed text-stone-100">
                <span className="mr-1.5 font-semibold text-amber-300">答案</span>
                {renderWithGloss(stripLatex(cleaned.answer))}
              </p>
            )}

            {cleaned.explanation && (
              <ExplanationDetails text={stripLatex(cleaned.explanation)} />
            )}

            {/* 开放题:诚实自评,才记账 */}
            {!isChoice && !graded && (
              <div className="mt-4 border-t border-white/5 pt-4">
                <p className="text-xs font-medium text-stone-400">
                  诚实自评:这题你答对了吗?
                </p>
                <div className="mt-2.5 flex flex-wrap gap-3">
                  <motion.button
                    type="button"
                    onClick={() => grade("correct")}
                    whileTap={reduce ? undefined : { scale: 0.96 }}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-400 px-5 py-2.5 text-sm font-bold text-[#062015] shadow-[0_10px_36px_-10px_rgba(52,211,153,0.5)] transition-colors duration-150 hover:bg-emerald-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090E0B]"
                  >
                    <Check className="size-4" />
                    我答对了
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => grade("wrong")}
                    whileTap={reduce ? undefined : { scale: 0.96 }}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-rose-400/50 bg-rose-500/10 px-5 py-2.5 text-sm font-semibold text-rose-200 transition-colors duration-150 hover:bg-rose-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090E0B]"
                  >
                    <X className="size-4" />
                    我答错了
                  </motion.button>
                </div>
              </div>
            )}

            {!isChoice && graded && (
              <motion.p
                initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={
                  reduce
                    ? { duration: 0.2 }
                    : { type: "spring", stiffness: 320, damping: 20 }
                }
                className={cn(
                  "mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold",
                  graded === "correct"
                    ? "bg-amber-300/15 text-amber-300"
                    : "bg-rose-400/15 text-rose-300",
                )}
              >
                {graded === "correct" ? (
                  <Check className="size-3.5" />
                ) : (
                  <X className="size-3.5" />
                )}
                已记录:{graded === "correct" ? "答对" : "答错"}
              </motion.p>
            )}
          </div>

          {(isChoice || graded) && <NextButton onNext={onNext} label={nextLabel} />}
        </motion.div>
      )}

      {/* 题面/答案卡 */}
      {question.media && (
        <div className="space-y-2">
          <div className="flex gap-1 rounded-lg border border-[#223329] bg-[#0D1510]/80 p-1 text-xs">
            {(
              [
                ["front", "题面卡"],
                ["back", "答案卡"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setSide(key)}
                className={cn(
                  "flex-1 rounded-md px-3 py-1.5 transition",
                  side === key
                    ? "bg-emerald-400/15 text-emerald-300"
                    : "text-stone-500 hover:text-stone-300",
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <CardImage
            key={side}
            src={side === "front" ? question.media.front : question.media.back}
            fallback={
              side === "front"
                ? question.media.front_fallback
                : question.media.back_fallback
            }
            alt={side === "front" ? "题面卡" : "答案卡"}
          />
        </div>
      )}
    </div>
  );
}

export default function QuizPage() {
  // ?q=<id> 深链直达指定题(同 /train?scene= 先例);未命中当前筛选时回落首题
  const searchParams = useSearchParams();
  const [data, setData] = useState<QuizResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [difficulty, setDifficulty] = useState("1");
  const [track, setTrack] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get("q"));
  const reduce = useReducedMotion();
  const record = useSyncExternalStore(
    subscribeRecord,
    getRecordSnapshot,
    () => EMPTY_RECORD,
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/quiz", { cache: "no-store" });
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled)
          setData({ error: String(e), total: 0, categories: [], questions: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const all = data?.questions || [];
    return all.filter(
      (q) =>
        (!category || q.category === category) &&
        (!difficulty || q.difficulty === Number(difficulty)) &&
        (!track || (track === "spot" ? !!q.spot : !q.spot)),
    );
  }, [data, category, difficulty, track]);

  // 默认落题(#eec4):无 ?q= 时优先「当前筛选下首个可玩决策题」——有牌桌且
  // hero_hand 可解析,首屏直接出真牌桌 + 169 起手牌全图,兑现「牌桌题库」的
  // 视觉承诺(判官 r2 验收:首屏牌桌/卡牌节点 ≥ 1);纯概念筛选或无决策题时
  // 回退 filtered[0]。用户显式点题(selectedId)永远最优先。
  const selected = useMemo(
    () =>
      filtered.find((q) => q.id === selectedId) ||
      filtered.find(
        (q) => q.spot && !q.spot.needs_review && heroCellLabel(q.spot.hero_hand) !== null,
      ) ||
      filtered[0] ||
      null,
    [filtered, selectedId],
  );

  const selectedIndex = useMemo(
    () => (selected ? filtered.findIndex((q) => q.id === selected.id) : -1),
    [filtered, selected],
  );

  /** 左栏题单虚拟化:切题时把当前题滚进可视区(原题单 4177 节点全量渲染→卡顿) */
  const listRef = useRef<VirtuosoHandle>(null);
  useEffect(() => {
    if (selectedIndex >= 0) {
      listRef.current?.scrollToIndex({ index: selectedIndex, align: "center" });
    }
  }, [selectedIndex]);

  const filteredAnswered = useMemo(
    () => filtered.reduce((n, q) => (record[q.id] ? n + 1 : n), 0),
    [filtered, record],
  );

  const stats = useMemo(() => getQuizStats(record, data?.questions), [record, data]);

  const onRecord = (id: string, result: QuizResult) => {
    writeRecord(id, result);
  };

  /** 沿当前 filtered 顺序推进,末尾回到第一题 */
  const goNext = () => {
    if (filtered.length === 0) return;
    const nextIdx = (Math.max(selectedIndex, 0) + 1) % filtered.length;
    setSelectedId(filtered[nextIdx].id);
  };

  const atEnd = filtered.length > 0 && selectedIndex === filtered.length - 1;

  const selectCls =
    "flex-1 rounded-lg border border-[#26372C] bg-[#0C130F] px-2.5 py-2 text-xs text-stone-200 outline-none transition focus:border-emerald-400/60 focus:ring-2 focus:ring-emerald-400/20";

  return (
    <DojoFrame>
      <Stagger className="space-y-8">
        {/* 抬头 */}
        <Rise>
          <header className="space-y-3">
            <p className="text-[11px] font-semibold tracking-[0.24em] text-amber-300/70">
              GTO DOJO · 题库
            </p>
            <h1
              className={cn(
                DISPLAY_FONT,
                "text-4xl font-extrabold tracking-tight text-stone-50 md:text-5xl",
              )}
            >
              牌桌题库
            </h1>
            <p className="max-w-[56ch] text-sm leading-relaxed text-stone-400">
              看题、作答、马上看解析;答完点「下一题」,沿着筛选一路刷到底。
            </p>
            <p className="font-mono text-xs tabular-nums text-stone-500">
              {data?.categories.length ?? "—"} 类 {data?.total ?? "—"} 题 · 已作答{" "}
              {stats.answered} · 答对 {stats.correct} · 正确率{" "}
              {stats.accuracy !== null ? `${Math.round(stats.accuracy * 100)}%` : "—"}
            </p>
          </header>
        </Rise>

        {data?.error && (
          <Rise>
            <p className="rounded-xl border border-rose-400/30 bg-[#1E1013]/90 px-4 py-3 text-sm text-rose-200">
              {data.error}
            </p>
          </Rise>
        )}

        {loading && (
          <Rise>
            <p className="font-mono text-xs tabular-nums text-stone-500">正在洗牌…</p>
          </Rise>
        )}

        {!loading && !data?.error && (
          <Rise>
            <div className="grid gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
              {/* 左栏:筛选 + 题单 */}
              <div className="space-y-3 self-start lg:sticky lg:top-6">
                <div className="flex gap-2">
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className={selectCls}
                  >
                    <option value="">全部类目</option>
                    {(data?.categories || []).map((c) => (
                      <option key={c.category} value={c.category}>
                        {c.category_name}({c.count})
                      </option>
                    ))}
                  </select>
                  <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                    className={selectCls}
                  >
                    <option value="">全部难度</option>
                    {[1, 2, 3].map((d) => (
                      <option key={d} value={d}>
                        {DIFFICULTY_LABEL[d]}
                      </option>
                    ))}
                  </select>
                </div>

                {/* track 分流:决策题(有牌桌)vs 概念题(无牌桌),让用户能专注练有牌桌的决策题 */}
                <div className="flex gap-1.5">
                  {(
                    [
                      ["", "全部"],
                      ["spot", "决策题"],
                      ["concept", "概念题"],
                    ] as const
                  ).map(([v, label]) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setTrack(v)}
                      className={cn(
                        "flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium transition",
                        track === v
                          ? "border-emerald-400/60 bg-emerald-400/15 text-emerald-200"
                          : "border-[#26372C] bg-[#0C130F] text-stone-400 hover:text-stone-200",
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="h-[70vh]">
                  {filtered.length === 0 ? (
                    <p className="rounded-lg border border-[#223329] px-3 py-6 text-center text-xs text-stone-600">
                      无匹配题目
                    </p>
                  ) : (
                    <Virtuoso
                      ref={listRef}
                      data={filtered}
                      computeItemKey={(_idx, q) => q.id}
                      style={{ height: "100%" }}
                      increaseViewportBy={{ top: 200, bottom: 400 }}
                      itemContent={(_idx, q) => (
                        <button
                          type="button"
                          onClick={() => setSelectedId(q.id)}
                          className={cn(
                            "mb-1.5 flex w-full items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors duration-150",
                            selected?.id === q.id
                              ? "border-emerald-400/60 bg-[#12241A]"
                              : "border-[#223329] bg-[#0E1712]/80 hover:border-[#35503E]",
                          )}
                        >
                          <span className="mt-1.5">
                            <StatusDot result={record[q.id]?.result} />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-[11px] text-stone-500">
                              {q.display_id} · {DIFFICULTY_LABEL[q.difficulty]}
                              {q.options.length === 0 && " · 开放"}
                            </span>
                            <span className="mt-0.5 line-clamp-2 block text-xs leading-snug text-stone-300">
                              {q.question}
                            </span>
                          </span>
                        </button>
                      )}
                    />
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-[10px] text-stone-600">
                  <span className="flex items-center gap-1">
                    <StatusDot result="correct" /> 答对
                  </span>
                  <span className="flex items-center gap-1">
                    <StatusDot result="wrong" /> 答错
                  </span>
                  <span className="flex items-center gap-1">
                    <StatusDot result="revealed" /> 已揭示(旧)
                  </span>
                  <span className="flex items-center gap-1">
                    <StatusDot /> 未作答
                  </span>
                </div>
              </div>

              {/* 右栏:进度 + 米色题卡 + 作答区 */}
              <div className="min-w-0">
                {selected ? (
                  <div className="space-y-5">
                    <div className="flex items-center gap-4">
                      <span className="shrink-0 font-mono text-xs tabular-nums text-stone-400">
                        第 {selectedIndex + 1} 题
                        <span className="text-stone-600"> / {filtered.length}</span>
                      </span>
                      <div
                        className="h-1 flex-1 overflow-hidden rounded-full bg-[#1A271E]"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={filtered.length}
                        aria-valuenow={selectedIndex + 1}
                      >
                        <motion.div
                          className="h-full w-full origin-left rounded-full bg-emerald-400"
                          initial={false}
                          animate={{
                            scaleX: (selectedIndex + 1) / Math.max(filtered.length, 1),
                          }}
                          transition={
                            reduce
                              ? { duration: 0 }
                              : { type: "spring", stiffness: 140, damping: 22 }
                          }
                        />
                      </div>
                      <span className="shrink-0 font-mono text-xs tabular-nums text-amber-300/90">
                        已作答 {filteredAnswered}
                      </span>
                    </div>

                    {/* 牌桌:有 spot 的题先把局发出来(needs_review 的牌型不自洽题跳过,避免牌桌与题干打架) */}
                    {selected.spot && !selected.spot.needs_review && (
                      <motion.div
                        key={`spot-${selected.id}`}
                        data-quiz-spot-table={selected.id}
                        initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={
                          reduce
                            ? { duration: 0.2 }
                            : { type: "spring", stiffness: 220, damping: 26 }
                        }
                      >
                        <PokerTable spot={selected.spot} />
                      </motion.div>
                    )}

                    {/* 起手牌全图(#eec4):你这手在 169 全域的位置——牌桌已亮明 hero 牌,
                        矩阵是题境视觉不是答案泄漏;hero_hand 缺失/不可解析 → 返 null 不渲染(禁编造) */}
                    {selected.spot && !selected.spot.needs_review && (
                      <HandMatrix169
                        key={`matrix-${selected.id}`}
                        heroHand={selected.spot.hero_hand}
                      />
                    )}

                    {/* 起手强度频率条(#937b):矩阵的量化读数——金色刻度 = 你这手在 1326 组合中的百分位 */}
                    {selected.spot && !selected.spot.needs_review && (
                      <FreqBar169
                        key={`freq-${selected.id}`}
                        heroHand={selected.spot.hero_hand}
                        className="mt-3"
                      />
                    )}

                    {/* 发牌:米色题卡落上绿毡 */}
                    <motion.div
                      key={`deal-${selected.id}`}
                      initial={
                        reduce
                          ? { opacity: 0 }
                          : { opacity: 0, y: -30, rotate: -2.5, scale: 0.97 }
                      }
                      animate={{ opacity: 1, y: 0, rotate: 0, scale: 1 }}
                      transition={
                        reduce
                          ? { duration: 0.2 }
                          : { type: "spring", stiffness: 240, damping: 26 }
                      }
                      className="relative rounded-[20px] bg-[linear-gradient(165deg,#F7F2E3,#ECE3CB)] p-6 shadow-[0_30px_90px_-24px_rgba(228,184,91,0.22),0_18px_44px_-12px_rgba(0,0,0,0.65)] ring-1 ring-black/50 md:p-7"
                    >
                      <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-medium">
                        <span className="rounded-md bg-[#173427] px-2 py-0.5 text-[#A7DCC0]">
                          {selected.category_name}
                        </span>
                        <span className="rounded-md bg-[#E4DCC4] px-2 py-0.5 text-[#5A6350]">
                          {DIFFICULTY_LABEL[selected.difficulty]}
                        </span>
                        <span className="rounded-md bg-[#E4DCC4] px-2 py-0.5 text-[#5A6350]">
                          {selected.display_id}
                        </span>
                        <span className="rounded-md bg-[#E4DCC4] px-2 py-0.5 text-[#5A6350]">
                          {selected.options.length > 0 ? "选择题" : "开放题"}
                        </span>
                      </div>
                      <h2 className="mt-4 text-balance text-[22px] font-bold leading-snug text-[#1B231C] md:text-[26px]">
                        {renderWithGloss(selected.question, "light")}
                      </h2>
                    </motion.div>

                    <AnswerPanel
                      key={selected.id}
                      question={selected}
                      onRecord={onRecord}
                      onNext={goNext}
                      nextLabel={atEnd ? "回到第一题" : "下一题"}
                    />

                    {/* 教练讲解:场景题 spot 的 GTO 推荐/为什么/其他选择/GTO vs 剥削,与「看解析」的选项级解释互补 */}
                    {selected.spot &&
                      (selected.spot.recommendation ||
                        selected.spot.reasoning ||
                        selected.spot.alternatives ||
                        selected.spot.gto_or_exploit) && (
                        <details className="group rounded-xl border border-amber-300/20 bg-[#1B1508]/40">
                          <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-xs font-medium text-amber-200/80 transition-colors hover:text-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-300/60 [&::-webkit-details-marker]:hidden">
                            <ChevronRight className="size-3.5 transition-transform duration-200 group-open:rotate-90" />
                            教练讲解
                          </summary>
                          <div className="space-y-3 border-t border-amber-300/10 px-4 py-3.5 text-sm leading-relaxed text-stone-300">
                            {selected.spot.recommendation && (
                              <p>
                                <span className="mr-1.5 font-semibold text-emerald-300">GTO 推荐</span>
                                {renderWithGloss(teachText(selected.spot.recommendation_zh ?? selected.spot.recommendation))}
                              </p>
                            )}
                            {selected.spot.reasoning && (
                              <p>
                                <span className="mr-1.5 font-semibold text-amber-300">为什么</span>
                                {renderWithGloss(teachText(selected.spot.reasoning_zh ?? selected.spot.reasoning))}
                              </p>
                            )}
                            {selected.spot.alternatives && (
                              <p>
                                <span className="mr-1.5 font-semibold text-rose-300">其他选择</span>
                                {renderWithGloss(teachText(selected.spot.alternatives))}
                              </p>
                            )}
                            {selected.spot.gto_or_exploit && (
                              <p>
                                <span className="mr-1.5 font-semibold text-stone-400">GTO vs 剥削</span>
                                {renderWithGloss(teachText(selected.spot.gto_or_exploit))}
                              </p>
                            )}
                          </div>
                        </details>
                      )}
                  </div>
                ) : (
                  <p className="rounded-2xl border border-[#223329] bg-[#0D1510]/80 py-12 text-center text-sm text-stone-600">
                    从左侧选择一道题开始
                  </p>
                )}
              </div>
            </div>
          </Rise>
        )}
      </Stagger>
    </DojoFrame>
  );
}
